import { cx, Fieldß, Inputß } from '@otter-ai/den-components';
import { useTranslations } from 'next-intl';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { EMAIL_REGEX } from '@/constants/constant';
import {
  getSessionItem,
  SESSION_STORAGE_KEYS,
  setSessionItem,
} from '@/shared/utils/sessionStorageUtils';

type EmailInputProps = {
  parentPage?: string;
  label?: string;
  className?: string;
  inputClassName?: string;
  fieldClassName?: string;
  customEmailError?: React.ReactNode | string;
  disabled?: boolean;
  saveInSessionStorage?: boolean;
  showError?: boolean;
};

export function EmailInput({
  parentPage,
  label,
  className,
  inputClassName,
  fieldClassName,
  customEmailError,
  disabled,
  saveInSessionStorage = true,
  showError = true,
}: EmailInputProps) {
  // Get the form methods from nearest FormProvider
  //   /signin/EmailLoginForm.tsx
  //   /forgot-password/page.tsx
  const {
    register,
    formState: { errors },
    setValue,
    watch,
    resetField,
  } = useFormContext();
  const t = useTranslations('auth.email_login');

  const emailValue = watch('email');

  // Load initial value from sessionStorage
  useEffect(() => {
    const savedEmail = getSessionItem(SESSION_STORAGE_KEYS.EMAIL_INPUT_STATE);

    if (savedEmail) {
      setValue('email', savedEmail, { shouldValidate: true });
    }
  }, [setValue]);

  // Save value to sessionStorage when it changes
  useEffect(() => {
    if (emailValue && saveInSessionStorage) {
      setSessionItem(SESSION_STORAGE_KEYS.EMAIL_INPUT_STATE, emailValue);
    }
  }, [emailValue, saveInSessionStorage]);

  return (
    <Fieldß
      as="Inputß"
      label={label}
      error={
        showError
          ? errors.email?.message?.toString() || customEmailError
          : undefined
      }
      className={fieldClassName}
    >
      <Inputß
        id={'email'}
        type="text"
        inputMode="email"
        data-testid={parentPage ? `${parentPage}-email-input` : 'email-input'}
        placeholder={t('email')}
        {...register('email', {
          required: t('email_required'),
          pattern: {
            value: EMAIL_REGEX,
            message: t('valid_email_required'),
          },
        })}
        onKeyDown={(e) => {
          e.stopPropagation();
        }}
        onBlur={(e) => {
          if (!e.target.value.trim()) {
            resetField('email', { defaultValue: '' });
          } else {
            register('email').onBlur(e);
          }
        }}
        aria-invalid={Boolean(errors.email)}
        aria-describedby="email-error"
        className={cx('w-full focus:border-primary-strong', className)}
        disabled={disabled}
        inputClassName={inputClassName}
        autoFocus
      />
    </Fieldß>
  );
}
